import { StateHandler } from "../util/getStateFun";
import { startLoader, stopLoader } from "../util/loader"
import { getCountryCode } from "../location-search-map/getCountryCode";
import { NearbyLocations } from "../util/nearbyLocations";
import { determineCountryZip, getBrandDetailsFromBrandJson, getFranchiseDetails, handleCrossDomainLink, isISNFlow, weblocationDetailsById } from "../util/share";
import { ScheduleService } from "../hms/hms-step1";
import { apiConfig } from "../config/apiConfig";
import { fetchNearbyLocationData } from "../util/common-neaybyLocations";

interface FranchiseDetails {
  locationWebsiteUrl?: string;
  alternateConcept: string;
  franchiseWebLocationId: string;
  doingBusinessAs: string;
  franchiseeName: string;
  websiteUrl?: string;
  distance?: number;
  phoneNumber?: string;
  websiteTrackingPhone?: string[];
  zipCode?: string;
}

export interface BrandDetail {
  enable_50mile_areameasure_findChangeMyLocation?: boolean;
  enable_50mile_areameasure?: boolean;
  area_measure_values?: string;
  area_measure_values_findChangeMyLocation?: string;
  nearest_franchiselimit?: number;
  nearest_franchiselimit_findChangeMyLocation?: number;
  include_crownpeakURL?: boolean;
  include_statename?: boolean;
  websitelink_findChangeMyLocation?: string;
}

export class GetLocateLocation {
  private static instance: GetLocateLocation;

  private constructor() { }

  public static getInstance(): GetLocateLocation {
    if (!GetLocateLocation.instance) {
      GetLocateLocation.instance = new GetLocateLocation();
    }
    return GetLocateLocation.instance;
  }

  // Helper method to safely get DOM elements
  private getElement<T extends HTMLElement>(id: string): T | null {
    return document.getElementById(id) as T;
  }

  // Helper method to safely get input value
  public getInputValue(id: string): string {
    const element = this.getElement<HTMLInputElement>(id);
    return element?.value ?? '';
  }

  // Helper method to safely query selector
  private querySelector<T extends HTMLElement>(selector: string): T | null {
    return document.querySelector(selector) as T;
  }

  // Helper method to store session data
   storeSessionData(key: string, value: string): void {
    sessionStorage.setItem(key, value);
  }

  // Helper method to store local data
  private storeLocalData(key: string, value: string): void {
    localStorage.setItem(key, value);
  }

  // Helper method to handle common storage operations for franchise details
  private storeFranchiseData(result: FranchiseDetails, sAddressParam: string): void {
    this.storeSessionData('alternateConcept', result.alternateConcept);
    this.storeLocalData('franchiseWebLocationId', result.franchiseWebLocationId);
    this.storeSessionData('countryCode', getCountryCode());
    this.storeLocalData('doingBusinessAs', result.doingBusinessAs);
    this.storeSessionData('franchiseeName', result.franchiseeName);
    this.storeSessionData('sAddressParam', sAddressParam);
  }

  // Helper method to handle URL redirection
  private handleRedirection(sRedirectURL: string): void {
    const hostURL = new URL(sRedirectURL);
    const pathname = hostURL.pathname;
    const redirectURL = new URL(window.location.href);
    const redirectHostName = redirectURL.host;
    window.location.replace(`https://${redirectHostName}${pathname}`);
  }

  // Helper method to create nearby locations instance and search
  public async searchNearbyLocations(units: string, franchiseLimit: number, inputArg: any): Promise<any> {
    const nearbyLocations = new NearbyLocations(units, franchiseLimit);
    return nearbyLocations.searchNearbyLocations(inputArg);
  }

  // Helper method to handle different nearby location result scenarios
  private async handleNearbyLocationResults(data: any, sAddressParam: string, brandDetail?: BrandDetail, units = "miles"): Promise<void> {
    const conceptId = this.getInputValue('conceptId');

    if (data?.length > 1) {
      this.openNearByLocationsModal(data, sAddressParam, brandDetail, units);
    } else if (data?.length === 1) {
      if (conceptId === '25') {
        const HMSStep1Instance = ScheduleService.getInstance();
        HMSStep1Instance.checkPOSSystemWithZip(sAddressParam);
      } else if (data[0]?.websiteUrl) {
        if (sAddressParam) {
          this.storeSessionData('sAddressParam', sAddressParam);
        }
        this.handleRedirection(data[0].websiteUrl);
      } else {
        this.handleBrandSpecificError();
      }
    } else {
      this.handleBrandSpecificError();
    }
  }

  // Consolidated method to handle nearby locations flow
  private handleNearbyLocationsFlow(inputArg: any): void {
    const conceptCode = this.getInputValue('conceptCode');

    getBrandDetailsFromBrandJson(conceptCode)
      .then((brandDetail) => {
        const findChange50mileflag = brandDetail?.enable_50mile_areameasure_findChangeMyLocation;
        const flag = brandDetail?.enable_50mile_areameasure;

        if (findChange50mileflag) {
          brandDetail?.use_miles_endPoint ? this.handleEnable50MilesFindMyLocationNewEndPoint(inputArg, brandDetail) : this.handleEnable50MilesFindMyLocation(inputArg, brandDetail);
        } else if (flag) {
          const units = brandDetail?.area_measure_values ? this.findUnits(brandDetail.area_measure_values) : 'miles';
          const franchiseLimit = brandDetail?.nearest_franchiselimit ? Number(brandDetail.nearest_franchiselimit) : 50;
          this.callNearbyLocations(units, franchiseLimit, inputArg);
        } else {
          this.handleBrandSpecificError();
        }
      })
      .catch((err: any) => {
        console.log('Unable to read brands.json. Using default range 50 miles radius...');
        this.callNearbyLocations('miles', 50, inputArg);
      });
  }

  // Consolidated method to call nearby locations
  private callNearbyLocations(units: string, franchiseLimit: number, inputArg: any): void {
    this.searchNearbyLocations(units, franchiseLimit, inputArg)
      .then((data: any) => {
        const brandData = {
          containingData: Boolean(data),
          franchiseLimit,
          units,
          resultData: data,
          searchQuery: inputArg
        };
        sessionStorage.setItem('nearbyLocations', JSON.stringify(brandData));
        this.openLocationsPage();
      })
      .catch(() => {
        console.log('Error searching nearby locations');
        this.handleBrandSpecificError();
      });
  }
  private openLocationsPage(): void {
    window.location.replace(window.location.origin + '/locations/');
  }

  private handleErrorScenario(): void {
    const modalElement = this.querySelector<HTMLElement>('#glb-find-local');
    modalElement?.classList.add('modal');

    const text1 = this.getElement<HTMLElement>('step1-text1');
    const text2 = this.getElement<HTMLElement>('step1-text2');

    if (text1) text1.style.display = 'none';
    if (text2) text2.style.display = 'block';
  }

  public findUnits(area_measure_values: string): string {
    return area_measure_values.includes('measure_km') ? 'km' : 'miles';
  }

  // Helper method to process address parameter based on device type
  private getAddressParam(sDeviceType: string): string {
    if (sDeviceType === 'desk') {
      return this.processDesktopAddress();
    } else if (sDeviceType === 'mob') {
      return this.processMobileAddress();
    }
    return '';
  }

  // Helper method to process desktop address input
  private processDesktopAddress(): string {
    const element = this.getElement<HTMLInputElement>('local-search-modal');
    const zipcode = element?.value?.trim();

    if (zipcode) {
      this.storeSessionData('temp_zipcode', zipcode);
    }

    if (!zipcode) return '';

    const domainName = location.hostname.toLowerCase();
    const isWebSite = domainName.indexOf('-ca-') === -1 &&
      domainName.indexOf('.ca') === -1 &&
      domainName.indexOf('-ca.') === -1 ? 'us' : 'ca';

    const countryZip = determineCountryZip(zipcode);

    if (isWebSite === 'ca' && countryZip === 'ca') {
      return zipcode.indexOf(' ') === -1 ? zipcode.replace(/^(.{3})(.*)$/, '$1 $2') : zipcode;
    } else if (zipcode.length > 0) {
      return zipcode;
    }
    return '';
  }

  // Helper method to process mobile address input
  private processMobileAddress(): string {
    const inputLocalZip = this.getValue('inputLocal-zip');
    return inputLocalZip.length > 0 ? inputLocalZip : '';
  }

  // Helper method to extract service ID from radio buttons
  private extractServiceId(): string | undefined {
    const findSection = this.querySelector('.find-local-modal');
    if (!findSection) return undefined;

    const radioButtons = findSection.querySelectorAll('input[type="radio"].glass-type-radio');
    if (radioButtons.length === 0) return undefined;

    for (const radioButton of radioButtons) {
      const inputElement = radioButton as HTMLInputElement;
      if (inputElement?.checked) {
        const radioButtonLabel = inputElement.closest('.glass-type');
        const serviceIdInput = radioButtonLabel?.querySelector('.modal-lead-service-id') as HTMLInputElement;

        if (serviceIdInput?.classList.contains('modal-lead-service-id') && serviceIdInput.value) {
          const serviceIdValue = serviceIdInput.value;
          this.storeSessionData('serviceIdValue', serviceIdValue);
          return serviceIdValue;
        }
      }
    }
    return undefined;
  }

  // Helper method to process franchise details response
  private async processFranchiseDetailsResponse(result: FranchiseDetails[], sAddressParam: string): Promise<void> {
    if (result.length === 0) return;

    stopLoader();
    const firstResult = result[0];
    const sRedirectURL = firstResult.locationWebsiteUrl ?? '';

    this.storeFranchiseData(firstResult, sAddressParam);

    const conceptId = this.getInputValue('conceptId');
    if (conceptId === '25') {
      this.storeLocalData('hms_redirect_local', 'true');
    }

    if (sRedirectURL) {
      handleCrossDomainLink(sRedirectURL);
    }

    const conceptCode = this.getInputValue('conceptCode');
    const brandDetail = await getBrandDetailsFromBrandJson(conceptCode);
    const crownPeakUrl = brandDetail?.include_crownpeakURL ?? false;

    if (crownPeakUrl || brandDetail?.include_statename) {
      this.redirectToWebLocation();
    } else if (sRedirectURL) {
      window.location.href = sRedirectURL;
    } else {
      this.redirectToWebLocation();
    }
  }

  // Helper method to handle location errors
  private async handleLocationError(err: any, sAddressParam: string): Promise<void> {
    if (typeof err?.then === 'function') {
      err = await err;
    }

    let message = err?.response?.data?.message ?? err?.message ?? '';

    if (message.toLowerCase() === 'full address is needed to narrow down results') {
      const stateHandler = new StateHandler();
      stateHandler.getStateFun();

      const step1 = this.querySelector<HTMLElement>('.step-1');
      const step2 = this.querySelector<HTMLElement>('.step-2');

      step1?.classList.add('hidden');

      if (step2) {
        step2.classList.remove('hidden');
        step2.classList.add('block');

        const closeLocalModal = this.querySelector('.close-modal-btn');
        closeLocalModal?.addEventListener('click', () => {
          step2.classList.add('hidden');
          step1?.classList.remove('hidden');
        });
      }

      const modalZipCode = this.querySelector<HTMLInputElement>('.step-2 #modal-zipcode');
      if (modalZipCode) {
        modalZipCode.value = sAddressParam;
      }
    } else {
      // Any other failure (e.g. a 400 with a different/no message) previously left the
      // UI showing nothing after the loader stopped. Fall back to the generic error modal.
      this.handleErrorScenario();
    }
  }

  public locateLocation(sDeviceType: string): void {
    const sAddressParam = this.getAddressParam(sDeviceType);
    const serviceIdValue = this.extractServiceId();

    startLoader();
    getFranchiseDetails(sAddressParam, false, serviceIdValue) // IsRoundRobin is false
      .then(async (response) => {
        if (response.length > 0) {
          await this.processFranchiseDetailsResponse(response, sAddressParam);
        } else {
          this.handleNearbyLocationsFlow(sAddressParam);
          stopLoader();
        }
      })
      .catch(async (err) => {
        stopLoader();
        await this.handleLocationError(err, sAddressParam);
      });
  }

  private getValue(id: string): string {
    return this.getInputValue(id);
  }

  private redirectToWebLocation(): void {
    const weblocation = weblocationDetailsById();
    if (weblocation?.weblocation_url) {
      window.location.replace(weblocation.weblocation_url);
    }
  }

  private async fetchNearbyLocations(units: string, franchiseLimit: number, brandDetail: BrandDetail): Promise<any> {
    let api = apiConfig.WebLocationsFranchiseGroupAPI;
    const zipValue = localStorage.getItem('zipcode');

    if (!zipValue) return null;

    // Convert to kilometers if required
    if (units === 'km') {
      franchiseLimit = this.milesToKilometers(franchiseLimit);
    }

    return fetchNearbyLocationData(api, zipValue, franchiseLimit, '#glb-find-local');
  }


  private handleEnable50MilesFindMyLocationNewEndPoint(sAddressParam: any, brandDetail: BrandDetail): void {
    const units = brandDetail?.area_measure_values_findChangeMyLocation?.includes('miles') ?? false ? 'miles' : 'km';
    let franchiseLimit = brandDetail?.nearest_franchiselimit_findChangeMyLocation ?? 50;
    this.fetchNearbyLocations(units, franchiseLimit, brandDetail).then(async (response) => {
      if (response?.length > 0) {
        await this.handleNearbyLocationResults(response, sAddressParam, brandDetail, units);
      } else {
        this.handleBrandSpecificError();
      }
    })
      .catch((error) => {
        console.error('Error fetching nearby locations:', error);
        this.handleBrandSpecificError();
      });
  }

    private handleEnable50MilesFindMyLocation(sAddressParam: any, brandDetail: BrandDetail): void {
    const units = 'miles';
    const franchiseLimit = brandDetail?.nearest_franchiselimit_findChangeMyLocation ?? 50;
    
    this.searchNearbyLocations(units, franchiseLimit, sAddressParam)
      .then(async (data: any) => {
        await this.handleNearbyLocationResults(data, sAddressParam, brandDetail, units);
      })
      .catch((err) => {
        console.log('Error searching nearby locations', err);
        this.handleBrandSpecificError();
      });
  }

  public milesToKilometers(miles: number): number {
    return Math.round(miles * 1.60934);
  }

  async handleBrandSpecificError() {
    const conceptId = (document.getElementById('conceptId') as HTMLInputElement)?.value;
    const fromHMS = sessionStorage.getItem('from-hms-nearby-location');
    if (conceptId === '25' && fromHMS === 'true') {
      const HMSStep1Instance = ScheduleService.getInstance();
      HMSStep1Instance.handleErrorModal();
      sessionStorage.setItem('from-hms-nearby-location', 'false');
    } else {
      this.handleErrorScenario();
    }
  }

  openNearByLocationsModal(resultData: any, zipCode: any, brandDetail: any, units = "miles") {
    stopLoader();
    const modal = document.getElementById("findnearby-user-zip-code");
    if (modal) {
      const totalResults = resultData?.length;
      const resultsTextElement = document.querySelector('.results-text');
      if (resultsTextElement) {
        resultsTextElement.textContent = ` ${totalResults} Results for ${zipCode}`;
      }
      const ul = document.querySelector('.user-zip-result-list') as HTMLElement;
      ul.innerHTML = '';
      const sortedResultData = resultData?.slice().sort((a: any, b: any) => a.distance - b.distance);
      sortedResultData?.forEach((item: any) => {
        const liElement = document.createElement('li');
        liElement.className = 'user-zip-result-item';
        if (units.includes('mi')) {
          units = 'mi.';
          item.distance = item.distance * 0.621371;
        }
        const distanceRounded = Math.round(item.distance) + ' ' + units;
        const pNum = item?.websiteTrackingPhone?.length > 0 ? item.websiteTrackingPhone[0] : item.phoneNumber;
        liElement.innerHTML = `
            <div class="locaion-info">
            <div class="locaion-time">
              <svg aria-hidden="true" class="icon">
                <use xlink:href="/brand/_assets/images/icons/hero-icon-sprite.svg#map-pin-primary"></use>
              </svg>
              <span class="location-text">${distanceRounded}</span>
            </div>
            <div class="locaion-name">
              <p class="location-main">${item?.doingBusinessAs}</p>
              <a class="location-call" href="tel:${pNum}">(${pNum?.slice(0, 3)}) ${pNum?.slice(3, 6)}-${pNum?.slice(6)}</a>
              <p class="location-zip-code hidden">${item?.zipCode}</p>
              <input type="hidden" class="location-weblocationid" value="${item?.franchiseWebLocationId}" />
              <input type="hidden" class="location-franchiseid" value="${item?.franchiseId}" />
              <input type="hidden" class="location-weblocationmarketingid" value="${item?.franchiseWebLocationMarketingId}" />
            </div>
          </div>
          ${item?.websiteUrl ?
            `<div class="locaion-btn">
          <a href=${item?.websiteUrl} class="primary-link">${brandDetail?.websitelink_findChangeMyLocation}</a>
          </div>`: ''}`;
        ul?.appendChild(liElement);
      });

      const closeModalButton = document.getElementById("close-local-modal");
      if (closeModalButton) {
        closeModalButton.click();
      }
      (document.querySelector('.findnearby-modal') as HTMLElement)?.click();
      const conceptCode = (document.getElementById('conceptId') as HTMLInputElement)?.value;
      if (conceptCode === '25') {
        const nearbyLocations = document.querySelectorAll('.user-zip-result-item > .locaion-btn a');
        nearbyLocations.forEach(item => {
          item.addEventListener('click', async (event) => {
            event.preventDefault();
            const listItem = (event.currentTarget as HTMLElement).closest(".user-zip-result-item");
            const franchiseWebLocationId = (listItem?.querySelector(".location-weblocationid") as HTMLInputElement)?.value?.trim() ?? "";
            const franchiseId = (listItem?.querySelector(".location-franchiseid") as HTMLInputElement)?.value?.trim() ?? "";
            const franchiseWebLocationMarketingId = (listItem?.querySelector(".location-weblocationmarketingid") as HTMLInputElement)?.value?.trim() ?? "";
            const locationText = listItem?.querySelector(".location-zip-code")?.textContent?.trim() ?? "";
            const zipRegex = /\b(?:\d{5}(?:-\d{4})?|[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d)\b/;
            const zipMatch = zipRegex.exec(locationText);
            if (zipMatch) {
              const zipcode = zipMatch[0];
              sessionStorage.setItem('sAddressParam', zipcode);
              const nearByLocDialog = document.getElementById('findnearby-user-zip-code');
              if (!nearByLocDialog?.classList.contains('hidden')) {
                (document.querySelector('.findnearby-modal') as HTMLElement)?.click();
              }
              isISNFlow({franchiseWebLocationId,franchiseId,franchiseWebLocationMarketingId},zipcode,(event.currentTarget as HTMLAnchorElement)?.href);

            } else {
              console.log("ZIP code not found in location text.");
            }
          });
        });
      }
    }
  }

  findMyChangeLocationRedirectAndLocalise(sAddressParam: any) {
    startLoader();
    getFranchiseDetails(sAddressParam, false) // IsRoundRobin is false
      .then((response) => {
        const result = response;
        if (result.length > 0) {
          stopLoader();
          const sRedirectURL = result[0].locationWebsiteUrl ?? '';
          sessionStorage.setItem('alternateConcept', result[0].alternateConcept);
          localStorage.setItem("franchiseWebLocationId", result[0].franchiseWebLocationId);
          let countryCode = getCountryCode();
          sessionStorage.setItem('countryCode', countryCode);
          localStorage.setItem('doingBusinessAs', result[0].doingBusinessAs);
          sessionStorage.setItem('franchiseeName', result[0].franchiseeName);
          sessionStorage.setItem('sAddressParam', sAddressParam)
          const hostURL = new URL(sRedirectURL);
          const pathname = hostURL.pathname;
          const redirectURL = new URL(window.location.href)
          const redirectHostName = redirectURL.host;
          window.location.replace(`https://${redirectHostName}${pathname}`);
        } else {
          stopLoader();
          this.handleBrandSpecificError();
        }
      }).catch((error) => {
        stopLoader();
        this.handleBrandSpecificError();
      });
  }

  public getNearbylocation(sAddressParam: any, brandDetail: any) {
    if(!brandDetail?.use_miles_endPoint){
      this.handleEnable50MilesFindMyLocation(sAddressParam, brandDetail);
    }else{
      localStorage.setItem('zipcode', sAddressParam);
      this.handleEnable50MilesFindMyLocationNewEndPoint(sAddressParam, brandDetail);
    }
  }
}